import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAclVersion } from '@/hooks/useSessionRefresh';
import { getApiCall } from '@/service/httpCall';
import { API_CONTACT_CUSTOM_FIELD_TEMPLATES } from '@/service/url';
import { currentUserHasContactCustomFields } from '@/utilities/contactCustomFieldsAcl';

import type { CustomFieldTemplate } from './customFields';

/** Contacts → Options → Custom fields, re-read when the ACL refreshes. */
export function useContactCustomFieldsAcl(): boolean {
  const aclVersion = useAclVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => currentUserHasContactCustomFields(), [aclVersion]);
}

/** Templates for the dropdowns; loads nothing when the ACL option is off. */
export function useCustomFieldTemplates() {
  const enabled = useContactCustomFieldsAcl();
  const [templates, setTemplates] = useState<CustomFieldTemplate[]>([]);
  // Starts true when enabled so callers can wait for the first fetch.
  const [loading, setLoading] = useState(enabled);

  const reload = useCallback(() => {
    if (!enabled) {
      setTemplates([]);
      return Promise.resolve();
    }
    setLoading(true);
    return getApiCall(API_CONTACT_CUSTOM_FIELD_TEMPLATES, {})
      // BIGINT ids come back as strings; the dropdowns compare numbers.
      .then((resp: unknown) =>
        setTemplates(Array.isArray(resp) ? (resp as CustomFieldTemplate[]).map((t) => ({ ...t, id: Number(t.id) })) : []),
      )
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { enabled, templates, loading, reload };
}
